import { DetailedHTMLProps, forwardRef, InputHTMLAttributes, useId } from 'react';
import { describedBy } from '../fieldMessages';

interface InputProps extends DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
    classNames?: string;
    error?: any;
    errorMessage?: string;
    infoMessage?: string;
    /** Announce changes of the message while the user types (e.g. the password strength) as a polite status */
    announceMessage?: boolean;
    hasIcon?: boolean;
    hideLabel?: boolean;
    label?: string;
    testId?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            children,
            classNames = '',
            errorMessage,
            infoMessage,
            announceMessage,
            hasIcon,
            hideLabel,
            label,
            testId,
            autoComplete,
            id,
            'aria-describedby': ariaDescribedBy,
            ...props
        }: InputProps,
        ref
    ) => {
        const generatedId = useId();
        const inputId = id ?? generatedId;
        const message = errorMessage || infoMessage;
        const messageId = `${inputId}-message`;
        if (!autoComplete) {
            autoComplete = 'off';
        }
        return (
            <div className={`group relative w-full clear-both ${classNames ? classNames : 'mb-10'}`}>
                <label data-testid={testId} htmlFor={inputId} className={`block text-sm ${hideLabel ? 'sr-only' : ''}`}>
                    {label}
                </label>
                <div className="flex justify-between">
                    <input
                        id={inputId}
                        className={`w-full bg-transparent outline-hidden ${hasIcon ? 'pl-12' : ''} py-input-vert text-base`}
                        autoComplete={autoComplete}
                        {...props}
                        aria-invalid={errorMessage ? true : undefined}
                        aria-describedby={describedBy(ariaDescribedBy, !!message && messageId)}
                        ref={ref}
                    />
                    {children}
                </div>
                <div
                    className={`input-border ${errorMessage ? 'bg-warning' : infoMessage ? 'bg-success' : 'bg-black group-hover:bg-primary group-focus-within:bg-primary'}`}
                />
                {/* One element for the message (error or info), so it can be a polite status region: that only announces
                    changes inside an element that stays in place, e.g. the password strength going from weak to strong. */}
                {(message || announceMessage) && (
                    <p
                        id={messageId}
                        role={announceMessage ? 'status' : undefined}
                        className={errorMessage ? 'error-message' : infoMessage ? 'w-full float-left text-success-700 text-sm mt-1 mb-2' : undefined}
                    >
                        {message}
                    </p>
                )}
            </div>
        );
    }
);
